/**
 * TC-F2 — Use Template multi-step flow.
 * Steps: Blueprint → Name/description → date anchor → capability overrides →
 * ATTACH-GOVERNANCE (skippable, default none) → create → land on defaultView.
 *
 * instantiate-v5_1 accepts projectName, startDate, and endDate.
 * Description, when present, is still PATCH /projects/:id after create.
 * Capabilities via PATCH .../capabilities (existing DTO).
 * selected policies via PUT /admin/governance/policies/:code when chosen.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { TemplateDto } from '@/features/templates/templates.api';
import { getPreview, instantiateV51, type PreviewResponse } from '@/features/templates/api';
import {
  readRecommendedPolicyBundle,
  readRecommendedPolicyCodes,
  resolvePostInstantiateProjectPath,
} from '@/features/templates/template.mapper';
import {
  DEFAULT_PROJECT_CAPABILITIES,
  type ProjectCapabilities,
  patchProjectCapabilities,
} from '@/features/projects/capabilities';
import { api } from '@/lib/api';
import { administrationApi, type WorkspaceGovernancePolicy } from '@/features/administration/api/administration.api';
import { POLICY_UI_META } from '@/features/administration/constants/governance-policies';
import { isPlatformAdmin } from '@/utils/access';
import { useAuth } from '@/state/AuthContext';
import { TemplateBlueprintStep } from './TemplateBlueprintStep';
import { useDialogBehavior } from '@/hooks/useDialogBehavior';
import { TEMPLATE_INSTANTIATE_DENIED_COPY } from '@/features/templates/templateCenterCopy';

type FlowStep = 'blueprint' | 'name' | 'dates' | 'capabilities' | 'governance';

/** B21: MVP governance is gates only. The policy step stays in source and is not shown. */
const SHOW_TEMPLATE_GOVERNANCE_POLICY_STEP = false;

const STEPS: FlowStep[] = SHOW_TEMPLATE_GOVERNANCE_POLICY_STEP
  ? ['blueprint', 'name', 'dates', 'capabilities', 'governance']
  : ['blueprint', 'name', 'dates', 'capabilities'];

const CAP_LABELS: Record<keyof ProjectCapabilities, string> = {
  use_phases: 'Phases',
  use_iterations: 'Iterations',
  use_gates: 'Gates',
  use_wip_limits: 'WIP limits',
};

function templateDurationDays(template: TemplateDto | null): number | null {
  const phases = template?.phases ?? [];
  if (phases.length === 0) return null;
  let sum = 0;
  for (const phase of phases) {
    const days = phase.estimatedDurationDays;
    if (typeof days !== 'number' || !Number.isFinite(days) || days <= 0) return null;
    sum += days;
  }
  // Inclusive: the last phase's due date is start + total duration − 1.
  return sum - 1;
}

function isEndDateValidationError(error: { code: string; message: string } | null): boolean {
  return error?.code === 'VALIDATION_ERROR' && /endDate/i.test(error.message);
}

function addCalendarDays(isoDate: string, days: number): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return '';
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** Catalog fallback when GET policies returns empty (e.g. Lean workspace with no rows yet). */
function catalogPoliciesFromMeta(): WorkspaceGovernancePolicy[] {
  return Object.entries(POLICY_UI_META).map(([code, meta]) => ({
    code,
    name: meta.displayName,
    description: meta.description,
    scope: 'workspace',
    severityEffective: 'WARN',
    source: 'disabled' as const,
    isEnabled: false,
    isEvaluable: code !== 'risk-threshold-alert' && code !== 'resource-capacity-governance',
  }));
}

export interface UseTemplateFlowModalProps {
  open: boolean;
  template: TemplateDto | null;
  workspaceId: string;
  loading?: boolean;
  error: { code: string; message: string } | null;
  onClose: () => void;
  onSuccess: (projectId: string, template: TemplateDto) => void;
  onError: (err: { code: string; message: string }) => void;
  /** Start at blueprint (Preview) or skip to name (direct Use). Default blueprint. */
  initialStep?: FlowStep;
  /** Structural write (workspace owner or organisation admin). Preview stays available when false. */
  canInstantiate?: boolean;
}

export function UseTemplateFlowModal({
  open,
  template,
  workspaceId,
  error,
  onClose,
  onSuccess,
  onError,
  initialStep = 'blueprint',
  canInstantiate = true,
}: UseTemplateFlowModalProps) {
  const { user } = useAuth();
  const isAdmin = isPlatformAdmin(user);

  const [step, setStep] = useState<FlowStep>(initialStep);
  const [projectName, setProjectName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endDateTouched, setEndDateTouched] = useState(false);
  const [capabilities, setCapabilities] = useState<ProjectCapabilities>({
    ...DEFAULT_PROJECT_CAPABILITIES,
  });
  const [policies, setPolicies] = useState<WorkspaceGovernancePolicy[]>([]);
  const [selectedPolicyCodes, setSelectedPolicyCodes] = useState<string[]>([]);
  const [policiesLoading, setPoliciesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<{ code: string; message: string } | null>(null);
  const [previewData, setPreviewData] = useState<PreviewResponse | null>(null);
  const dialogRef = useDialogBehavior(open && !submitting, onClose);

  const recommendedCodes = useMemo(
    () => (template ? readRecommendedPolicyCodes(template) : []),
    [template],
  );
  const recommendedBundle = useMemo(
    () => (template ? readRecommendedPolicyBundle(template) : null),
    [template],
  );

  /** API rows when present; otherwise full POLICY_UI_META catalog for Lean empty responses. */
  const policyCatalog = useMemo(
    () => (policies.length > 0 ? policies : catalogPoliciesFromMeta()),
    [policies],
  );
  const allPoliciesDisabled =
    policyCatalog.length === 0 || policyCatalog.every((p) => !p.isEnabled);

  const leanGovernanceCopy =
    'No policies are enabled in this workspace (Lean mode). Select policies below to enable them for this workspace';

  useEffect(() => {
    if (!open || !template) return;
    setStep(initialStep);
    setProjectName(template.name || '');
    setDescription('');
    setStartDate('');
    setEndDate('');
    setEndDateTouched(false);
    setCapabilities({
      ...(template.capabilities ?? DEFAULT_PROJECT_CAPABILITIES),
    });
    setSelectedPolicyCodes([]);
    setSubmitting(false);
    setPreviewData(null);
    setPreviewError(null);
  }, [open, template, initialStep]);

  useEffect(() => {
    if (isEndDateValidationError(error)) setStep('dates');
  }, [error]);

  useEffect(() => {
    if (!open || !template || step !== 'blueprint') return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    getPreview(template.id)
      .then((data) => {
        if (!cancelled) setPreviewData(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const e = err as { response?: { data?: { code?: string; message?: string } }; message?: string };
        setPreviewError({
          code: e?.response?.data?.code || 'PREVIEW_FAILED',
          message:
            e?.response?.data?.message || e?.message || 'Failed to load template preview',
        });
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, template, step]);

  useEffect(() => {
    if (!open || !workspaceId || step !== 'governance') return;
    let cancelled = false;
    setPoliciesLoading(true);
    administrationApi
      .listWorkspaceGovernancePolicies(workspaceId)
      .then((rows) => {
        if (!cancelled) setPolicies(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setPolicies([]);
      })
      .finally(() => {
        if (!cancelled) setPoliciesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, workspaceId, step]);

  if (!open || !template) return null;

  const stepIndex = STEPS.indexOf(step);

  const goNext = () => {
    if (step === 'name' && !projectName.trim()) return;
    const next = STEPS[stepIndex + 1];
    if (next) setStep(next);
  };

  const goBack = () => {
    const prev = STEPS[stepIndex - 1];
    if (prev) setStep(prev);
  };

  const togglePolicy = (code: string) => {
    setSelectedPolicyCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const handleCreate = async () => {
    if (!projectName.trim()) {
      onError({ code: 'MISSING_PROJECT_NAME', message: 'Project name is required' });
      return;
    }
    if (!workspaceId) {
      onError({
        code: 'WORKSPACE_REQUIRED',
        message: 'A workspace is required to create a project',
      });
      return;
    }

    setSubmitting(true);
    try {
      const result = await instantiateV51(template.id, {
        projectName: projectName.trim(),
        description: description.trim() || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        capabilities,
      });

      if (description.trim()) {
        try {
          await api.patch(`/projects/${result.projectId}`, { description: description.trim() });
        } catch (err: unknown) {
          const e = err as { response?: { data?: { code?: string; message?: string } }; message?: string };
          onError({
            code: e?.response?.data?.code || 'PROJECT_UPDATE_FAILED',
            message:
              e?.response?.data?.message ||
              e?.message ||
              'Failed to save the project description',
          });
          return;
        }
      }

      // Capability overrides via existing UpdateCapabilitiesDto.
      try {
        await patchProjectCapabilities(workspaceId, result.projectId, capabilities);
      } catch {
        // Owner-only endpoint — template copy-down already applied capabilities.
      }

      // ATTACH-GOVERNANCE: enable only explicitly selected policies (default none).
      // B21: hidden. The template's own gates apply; do not choose or save policies here.
      if (SHOW_TEMPLATE_GOVERNANCE_POLICY_STEP && isAdmin && selectedPolicyCodes.length > 0) {
        for (const code of selectedPolicyCodes) {
          try {
            await administrationApi.updateWorkspaceGovernancePolicy(code, {
              workspaceId,
              isEnabled: true,
            });
          } catch {
            // Non-blocking — project already exists.
          }
        }
      }

      onSuccess(result.projectId, template);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { code?: string; message?: string } }; message?: string };
      onError({
        code: e?.response?.data?.code || 'INSTANTIATE_FAILED',
        message:
          e?.response?.data?.message ||
          e?.message ||
          'Failed to create project from template',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const stepLabel = (s: FlowStep): string => {
    switch (s) {
      case 'blueprint':
        return 'Preview';
      case 'name':
        return 'Name';
      case 'dates':
        return 'Dates';
      case 'capabilities':
        return 'Options';
      case 'governance':
        return 'Policies';
      default:
        return s;
    }
  };

  // Create is the last visible step. Policies is hidden (B21), so Options is last
  // and must create — binding create only to governance left Continue as a no-op.
  const isLastVisibleStep = stepIndex === STEPS.length - 1;
  const primaryLabel = isLastVisibleStep
    ? submitting
      ? 'Creating…'
      : 'Create project'
    : 'Continue';

  return (
    <div className="fixed inset-0 z-[5100] overflow-y-auto" data-testid="use-template-flow-modal">
      <div className="flex min-h-screen items-center justify-center px-4 py-8 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-slate-900/40 transition-opacity duration-150"
          onClick={submitting ? undefined : onClose}
          aria-hidden
        />

        <div
          ref={dialogRef}
          className="inline-block w-full max-w-lg transform overflow-hidden rounded-xl border border-slate-200 bg-white text-left align-bottom shadow-sm transition-all duration-150 sm:my-8 sm:align-middle"
          role="dialog"
          aria-modal="true"
          aria-labelledby="use-template-flow-title"
          tabIndex={-1}
        >
          <div className="border-b border-slate-200 px-6 py-4">
            <h3 id="use-template-flow-title" className="text-lg font-semibold text-slate-900">Use template</h3>
            <p className="mt-1 truncate text-sm text-slate-600">{template.name}</p>
            <ol className="mt-3 flex flex-wrap gap-2 text-[11px] font-medium text-slate-500" data-testid="use-template-flow-steps">
              {STEPS.map((s, i) => (
                <li
                  key={s}
                  className={`rounded-full px-2.5 py-0.5 transition-colors duration-150 ${
                    s === step
                      ? 'bg-blue-100 text-blue-800'
                      : i < stepIndex
                        ? 'bg-slate-100 text-slate-700'
                        : 'bg-slate-50'
                  }`}
                >
                  {stepLabel(s)}
                </li>
              ))}
            </ol>
          </div>

          <div className="px-6 py-4">
            {error && !isEndDateValidationError(error) && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-sm text-red-800">{error.message}</p>
              </div>
            )}

            {step === 'blueprint' && (
              <TemplateBlueprintStep
                template={template}
                previewLoading={previewLoading}
                previewError={previewError}
                previewData={previewData}
              />
            )}

            {step === 'name' && (
              <div className="space-y-4" data-testid="use-template-step-name">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    Project name
                  </label>
                  <input
                    type="text"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    disabled={submitting}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    autoFocus
                    data-testid="use-template-name-input"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    Description <span className="font-normal text-slate-400">(optional)</span>
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={submitting}
                    rows={3}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    data-testid="use-template-description-input"
                  />
                </div>
              </div>
            )}

            {step === 'dates' && (
              <div className="space-y-4" data-testid="use-template-step-dates">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    Project start date <span className="font-normal text-slate-400">(optional)</span>
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      const next = e.target.value;
                      setStartDate(next);
                      if (!endDateTouched) {
                        const duration = templateDurationDays(template);
                        setEndDate(next && duration ? addCalendarDays(next, duration) : '');
                      }
                    }}
                    disabled={submitting}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    data-testid="use-template-start-date"
                  />
                  <p className="mt-2 text-xs text-slate-500">
                    Anchors the project timeline. Leave blank to set later.
                  </p>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="use-template-end-date">
                    Project end date <span className="font-normal text-slate-400">(optional)</span>
                  </label>
                  <input
                    id="use-template-end-date"
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                      setEndDateTouched(true);
                      setEndDate(e.target.value);
                    }}
                    disabled={submitting}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    data-testid="use-template-end-date"
                    aria-invalid={isEndDateValidationError(error) || undefined}
                    aria-describedby={isEndDateValidationError(error) ? 'use-template-end-date-error' : undefined}
                  />
                  {isEndDateValidationError(error) ? (
                    <p
                      id="use-template-end-date-error"
                      className="mt-2 text-xs text-red-700"
                      data-testid="use-template-end-date-error"
                    >
                      {error?.message}
                    </p>
                  ) : (
                    <p className="mt-2 text-xs text-slate-500">
                      Prefilled from the start date and the template duration. Change it if this project should end sooner or later.
                    </p>
                  )}
                </div>
              </div>
            )}

            {step === 'capabilities' && (
              <div className="space-y-3" data-testid="use-template-step-capabilities">
                <p className="text-sm text-slate-600">
                  Defaults come from the template. Adjust only if this project needs different options.
                </p>
                {(Object.keys(CAP_LABELS) as Array<keyof ProjectCapabilities>).map((key) => (
                  <label
                    key={key}
                    className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  >
                    <span className="font-medium text-slate-800">{CAP_LABELS[key]}</span>
                    <input
                      type="checkbox"
                      checked={capabilities[key]}
                      onChange={(e) =>
                        setCapabilities((prev) => ({ ...prev, [key]: e.target.checked }))
                      }
                      disabled={submitting}
                      data-testid={`use-template-cap-${key}`}
                    />
                  </label>
                ))}
              </div>
            )}

            {step === 'governance' && (
              <div className="space-y-3" data-testid="use-template-step-governance">
                <p className="text-sm text-slate-600">
                  Optionally attach workspace policies. Default is none — skip to keep workspace defaults.
                </p>
                {allPoliciesDisabled ? (
                  <p
                    className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700"
                    data-testid="use-template-governance-lean-copy"
                  >
                    {leanGovernanceCopy}
                  </p>
                ) : null}
                {recommendedBundle ? (
                  <p
                    className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700"
                    data-testid="use-template-governance-recommendation"
                  >
                    Template suggests the {recommendedBundle} policy posture. Nothing is selected by default.
                  </p>
                ) : null}
                {policiesLoading ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                  </div>
                ) : !isAdmin ? (
                  <p
                    className="text-xs text-slate-500"
                    data-testid="use-template-governance-non-admin"
                  >
                    {allPoliciesDisabled
                      ? 'No policies are enabled in this workspace (Lean mode). An admin can enable policies for this workspace.'
                      : 'Policy changes require an admin. You can skip this step.'}
                  </p>
                ) : (
                  <ul
                    className="max-h-56 space-y-2 overflow-y-auto"
                    data-testid="use-template-policy-list"
                  >
                    {policyCatalog.map((p) => {
                      const recommended = recommendedCodes.includes(p.code);
                      return (
                        <li key={p.code}>
                          <label
                            className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2 text-sm ${
                              recommended ? 'border-slate-300 bg-slate-50' : 'border-slate-200'
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="mt-1"
                              checked={selectedPolicyCodes.includes(p.code)}
                              onChange={() => togglePolicy(p.code)}
                              disabled={submitting}
                              data-testid={`use-template-policy-${p.code}`}
                            />
                            <span className="min-w-0 flex-1 text-left">
                              <span className="block font-medium text-slate-800">{p.name}</span>
                              <span className="block text-xs text-slate-500">{p.description}</span>
                              {recommended ? (
                                <span className="mt-1 inline-block text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                                  Suggested
                                </span>
                              ) : null}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-between gap-3 border-t border-slate-100 bg-slate-50 px-6 py-4">
            <button
              type="button"
              onClick={stepIndex === 0 ? onClose : goBack}
              disabled={submitting}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors duration-150 hover:bg-slate-50 disabled:opacity-50"
            >
              {stepIndex === 0 ? 'Cancel' : 'Back'}
            </button>
            <div className="flex gap-2">
              {canInstantiate && step === 'governance' ? (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedPolicyCodes([]);
                    void handleCreate();
                  }}
                  disabled={submitting}
                  className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors duration-150 hover:bg-slate-50 disabled:opacity-50"
                  data-testid="use-template-skip-governance"
                >
                  Skip & create
                </button>
              ) : null}
              {canInstantiate ? (
              <button
                type="button"
                onClick={() => {
                  if (isLastVisibleStep) void handleCreate();
                  else goNext();
                }}
                disabled={
                  submitting ||
                  (step === 'name' && !projectName.trim()) ||
                  (step === 'blueprint' && previewLoading)
                }
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="use-template-flow-primary"
              >
                {primaryLabel}
              </button>
              ) : (
                <p className="max-w-xs text-right text-xs text-slate-500" data-testid="use-template-instantiate-denied">
                  {TEMPLATE_INSTANTIATE_DENIED_COPY}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Re-export helper for tests / callers that navigate after success. */
export { resolvePostInstantiateProjectPath };
